import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { payoutsAPI, accountsAPI, fmtError } from '@/lib/api';
import { formatMoney, formatInt, prettyPhase } from '@/lib/format';

const CRYPTOS = [
  { code: 'btc', name: 'Bitcoin (BTC)', networks: ['BTC'] },
  { code: 'eth', name: 'Ethereum (ETH)', networks: ['ERC-20'] },
  { code: 'usdt', name: 'Tether (USDT)', networks: ['TRC-20', 'ERC-20', 'BEP-20'] },
  { code: 'usdc', name: 'USD Coin (USDC)', networks: ['ERC-20', 'BEP-20', 'Solana'] },
  { code: 'sol', name: 'Solana (SOL)', networks: ['SOL'] },
  { code: 'ltc', name: 'Litecoin (LTC)', networks: ['LTC'] },
  { code: 'trx', name: 'TRON (TRX)', networks: ['TRX'] },
];

export default function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [accs, setAccs] = useState([]);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('');
  const [form, setForm] = useState({ account_id: '', amount: '', cryptocurrency: 'usdt', wallet: '', network: 'TRC-20' });
  const [params] = useSearchParams();
  const loc = useLocation();

  const load = async () => {
    try {
      const [p, a] = await Promise.all([payoutsAPI.mine(), accountsAPI.mine()]);
      setPayouts(p); setAccs(a);
      if (params.get('new') === '1' && loc.state?.account_id) {
        setForm(f => ({ ...f, account_id: loc.state.account_id }));
      }
    } catch (e) { setErr(fmtError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setStatus('');
    try {
      await payoutsAPI.request({ ...form, amount: Number(form.amount) });
      setStatus('Reward request submitted — admin will review.');
      setForm({ account_id: '', amount: '', cryptocurrency: 'usdt', wallet: '', network: 'TRC-20' });
      load();
    } catch (e2) { setErr(fmtError(e2)); }
  };

  const fundedAccs = accs.filter(a => a.phase === 'funded' && a.status === 'active');

  return (
    <div className="space-y-6">
      <div>
        <div className="label-sm mb-2">Performance rewards</div>
        <h1 className="font-display text-3xl">Request your reward</h1>
        <p className="text-zinc-500 text-sm mt-1">Performance accounts can request a reward after the 14-day cycle. Rewards are distributed in cryptocurrency within 24–72 hours of approval.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-tech rounded-lg p-6">
          <div className="label-sm">Request a performance reward</div>
          {fundedAccs.length === 0 ? (
            <p className="text-sm text-zinc-500 mt-3">You don't have any performance accounts yet.</p>
          ) : (
            <form onSubmit={submit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payout-form">
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Performance account</div>
                <select className="input-tech" required value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="payout-account">
                  <option value="">Select account…</option>
                  {fundedAccs.map(a => (
                    <option key={a.account_id} value={a.account_id}>{`#${a.mt_login} · $${formatInt(a.account_size)} · ${prettyPhase(a.phase)}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="label-xs mb-1">Reward amount (USD)</div>
                <input className="input-tech" type="number" step="0.01" min="1" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="payout-amount" />
              </div>
              <div>
                <div className="label-xs mb-1">Cryptocurrency</div>
                <select className="input-tech" value={form.cryptocurrency} onChange={e => setForm({ ...form, cryptocurrency: e.target.value, network: CRYPTOS.find(c => c.code === e.target.value)?.networks[0] || 'TRC-20' })} data-testid="payout-crypto">
                  {CRYPTOS.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <div className="label-xs mb-1">Network</div>
                <select className="input-tech" value={form.network} onChange={e => setForm({ ...form, network: e.target.value })} data-testid="payout-network">
                  {(CRYPTOS.find(c => c.code === form.cryptocurrency)?.networks || ['TRC-20']).map(n => <option key={n}>{n}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Reward wallet address</div>
                <input className="input-tech font-mono" required value={form.wallet} onChange={e => setForm({ ...form, wallet: e.target.value })} placeholder="Your wallet address" data-testid="payout-wallet" />
              </div>
              {err && <div className="sm:col-span-2 text-sm text-[#ff3366]" data-testid="payout-error">{err}</div>}
              {status && <div className="sm:col-span-2 text-sm text-[#00ff66]" data-testid="payout-status">{status}</div>}
              <div className="sm:col-span-2">
                <button className="btn-primary" data-testid="submit-payout">Submit reward request</button>
              </div>
            </form>
          )}
        </div>
        <div className="card-tech rounded-lg p-6">
          <div className="label-sm">Reward policy</div>
          <ul className="mt-3 space-y-2 text-sm text-zinc-400">
            <li>• First reward: 14 days from first trade on performance account (21 days on Instant).</li>
            <li>• Subsequent rewards every 14 days.</li>
            <li>• Minimum reward request $50.</li>
            <li>• Rewards paid in BTC, ETH, USDT, USDC and more.</li>
            <li>• Reward split up to 70% based on performance.</li>
          </ul>
        </div>
      </div>

      <div className="card-tech rounded-lg p-6">
        <div className="label-sm">Reward history</div>
        {payouts.length === 0 ? <p className="text-sm text-zinc-500 mt-3">No reward requests yet.</p> : (
          <div className="mt-4 divide-y divide-white/5">
            {payouts.map(p => (
              <div key={p.payout_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`payout-row-${p.payout_id}`}>
                <div>
                  <div className="font-mono">${formatMoney(p.amount)} <span className="text-zinc-500 text-xs">{p.cryptocurrency?.toUpperCase()} · {p.network}</span></div>
                  <div className="text-xs text-zinc-500 mt-0.5">{new Date(p.created_at).toLocaleString()}</div>
                </div>
                <span className={`chip ${p.status === 'pending' ? 'chip-yellow' : p.status === 'approved' || p.status === 'paid' ? 'chip-green' : 'chip-red'}`}>{p.status.toUpperCase()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
